import { computed, ref, reactive } from 'vue-demi';
import {
  ChartMetricList,
  LevelFields,
  Level,
  CreativeType,
  getCampaignStatus,
  TimeRangeType,
  DayType,
  LifetimeType,
} from './../../../../const/dashboard';
import { getAccountToday, TIME_FORMAT } from './../../../../utils/dateUtil';
import moment from 'moment';
import DashboardState from './../../../../store/modules/dashboard/index';
import { completeImageUrl } from './../../../../utils/creativeUtil';
import store from './../../../../store/index';
import { getAccountSignTime } from './../../../../utils/dateUtil';
import { sessionStorageGetItem, sessionStorageSetItem } from './../../../../utils/localStorage';
import { DEFAULT_STATUS, SYSTEM_ORIGIN_MAP } from './../const/index';
import campaignStore from './../../../../store/modules/campaign/index';
import { formatNumberByLangCodeUtil } from './../utils/index';

export default function useCampaignList() {
  const loadingList = ref(true);
  const CAMPAIGN_CALENDAR_COOKIE = 'campaignCalendarCookie';
  const cookie_value = ref(sessionStorageGetItem(CAMPAIGN_CALENDAR_COOKIE));
  const startTime = computed(() => cookie_value.value?.split('|')[1]);
  const endTime = computed(() => cookie_value.value?.split('|')[2]);

  /** Getters from state and define payload constants */
  const lifeTime = computed(() => store.getters['dashboard/campaignLifetime']);
  const selectedRange = computed(() => store.getters['dashboard/campaignCalendar']);
  const sortStat = computed(() => store.getters['dashboard/sortStat']);
  const sortOrder = computed(() => store.getters['dashboard/sortOrder']); // 0 ascending, 1 descending
  const version = 1; // At that time, the backend added this version field to distinguish it from the domestic interface
  const baseParams = reactive({
    st: startTime,
    et: endTime,
    lifetime: lifeTime,
    sort_stat: sortStat,
    sort_order: sortOrder,
    version
  });

  const searchKeyword = ref('');
  const showEmpty = ref(false);
  const loading = ref(true);
  const pageOption = reactive({
    page: ref(0), // current page
    limit: ref(20), // items per page
    pageTotal: ref(0), // total pages
  });
  const rawTableResponse = ref({ table: [] });
  const statusFilters = computed(() => campaignStore.getters.campaignListData.value.statusFilter);
  const sourceFilters = computed(() => campaignStore.getters.campaignListData.value.sourceFilters);
  const objectiveFilters = computed(() => campaignStore.getters.campaignListData.value.objectiveFilter);
  const CREATE_TIME = computed(() => campaignStore.getters.campaignListData.value.campaignList[0]?.createTime); // Campaign creation timestamp

  function resetList(): void {
    pageOption.page = 0;
    pageOption.pageTotal = 0;
    campaignStore.mutations.setCampaignDataList([]);
  }
  function setTimeInfo(range: string, st: string, et: string, lifetime: LifetimeType): void {
    store.commit('dashboard/SET_CAMPAIGN_CALENDAR', range);
    store.commit('dashboard/SET_CAMPAIGN_CALENDAR_START_TIME', st);
    store.commit('dashboard/SET_CAMPAIGN_CALENDAR_END_TIME', et);
    store.commit('dashboard/SET_CAMPAIGN_CALENDAR_LIFETIME', lifetime);
  }
  function applyFilters(): any[] {
    let filteredTable;
    const { value: statuses } = statusFilters;
    const { value: objectives } = objectiveFilters;
    function handleNoObjectives(): void {
      if (!statusFilters.value.includes(DEFAULT_STATUS)) {
        filteredTable = rawTableResponse.value.table
          .filter(item => statuses.includes(item.campaign_primary_status));
      } else {
        // Status: All, Objective: All
        filteredTable = rawTableResponse.value.table;
      }
    }

    function handleOnlyObjectives(): void {
      filteredTable = rawTableResponse.value.table.filter(item => objectives.includes(item.objective_type));
    }

    function handleStatusAndObjectives(): void {
      // i.e Objectve: [2,3,5] , Status: ["Active", "no_delivery"]
      filteredTable = rawTableResponse.value.table
        .filter(item => statuses.includes(item.campaign_primary_status))
        .filter(item => objectives.includes(item.objective_type));
    }

    /**
     * if no objective filters applied, then filter statuses
     * else filter by objectives, else filter by both
    **/
    objectives.length === 0
      ? handleNoObjectives()
      : statuses[0] === DEFAULT_STATUS
        ? handleOnlyObjectives()
        : handleStatusAndObjectives();
    return filteredTable;
  }

  function handleTotalPages(pagination: any): void {
    pageOption.pageTotal = pagination.page_count || 0;
  }

  function handleDataList(list: Array<any>, level: string): Array<any> {
    if (list.length === 0) {
      const nextDataList = campaignStore.getters.campaignListData.value.campaignList.map((campaign: any) => {
        const defaultMetrics = {} as any;
        ChartMetricList.forEach(p => {
          defaultMetrics[p.label] = 0;
        });
        const newCampaign = {
          ...campaign,
          ...defaultMetrics,
        };
        return newCampaign;
      });
      campaignStore.mutations.setCampaignDataList(nextDataList);
      return nextDataList;
    }

    return list.map(item => {
      const metrics = {} as any;
      ChartMetricList.forEach(p => {
        metrics[p.label] = item.stat_data[p.field];
      });
      const { id, name } = LevelFields[level];
      return {
        ...metrics,
        id: item[id],
        name: item[name],
        status: getCampaignStatus(item.primary_status), // This status is the primary_status field indicating the first-level status of delivery, not the field of whether opt_status/status delivery is enabled
        switch: item.opt_status === 0, // 0 open， 1 closed
        imgUrl: completeImageUrl(item?.image_info?.[0]?.url || item?.image_info?.[0]?.web_uri || ''), // Compatible with tt material cover image
        isProcedural: item.creative_material_mode === CreativeType.PROCEDURAL,
        imageMode: item.image_mode,
        optimize_goal: item.optimize_goal,
        createTime: item.create_time * 1000,
        cost: formatNumberByLangCodeUtil(item.stat_data.stat_cost) || 0,
        impression: formatNumberByLangCodeUtil(item.stat_data.show_cnt) || 0,
      };
    });
  }
  const getCampaignList = async (): Promise<any> => {
    const { actions, state } = DashboardState;
    showEmpty.value = false;
    loading.value = true;
    pageOption.page = pageOption.page + 1;
    pageOption.page === 1 && campaignStore.mutations.setCampaignDataList([]);
    const systemOriginFilter = sourceFilters.value.length ? { campaign_system_origin: sourceFilters.value.reduce((acc, item) => { acc = [...acc, ...SYSTEM_ORIGIN_MAP[item]]; return acc; }, []) } : { campaign_system_origin: SYSTEM_ORIGIN_MAP.TTAM }
    const reqParams = {
      ...baseParams,
      ...pageOption,
      ...systemOriginFilter,
      query_list: ChartMetricList.map(item => item.field),
      sort_stat: sortStat.value,
      sort_order: sortOrder.value,
      campaign_status: statusFilters.value,
      keyword: searchKeyword.value,
      search_type: 1,
    };
    try {
      const { data = {} } = await actions.getCampaignList(state, reqParams);
      rawTableResponse.value = data;
      /**
       apply filters here so that after the first filter applies
       if there's one result, intersection observer will be triggered,
       and will call api again on the next page of campaigns. Therefore,
       we need to apply the filters here from there on out
       **/
      const filteredTable = applyFilters();
      handleTotalPages(data.pagination || {});
      const list = handleDataList(filteredTable || [], Level.campaign);
      const nextCampaignList =
        pageOption.page === 1 ? list : campaignStore.getters.campaignListData.value.campaignList.concat(list);

      // adding display list
      campaignStore.mutations.setCampaignDataList(nextCampaignList);
      loading.value = false;
      showEmpty.value = !nextCampaignList.length;
    } catch (e) {
      console.log(e);
    }
  };

  async function sortList(): Promise<any> {
    resetList();
    try {
      await getCampaignList();
    } catch (e) {
      console.log('Error in fetching campagin list with sorting applied: ', e);
    }
  }

  function today(): any {
    return getAccountToday();
  }

  function yesterday(): any {
    return moment(today()).subtract(1, 'days').format(TIME_FORMAT);
  }

  function last7Days(): any {
    return moment(today()).subtract(7, 'days').format(TIME_FORMAT);
  }

  function dayType(): number {
    return selectedRange.value === TimeRangeType.yesterday.i18n ? DayType.breakdown : DayType.none;
  }

  function signTime(): any {
    return getAccountSignTime();
  }

  function updateDataList(nextCampaignDataList): void {
    campaignStore.mutations.setCampaignDataList(nextCampaignDataList);
  }

  async function updateListWithFilters(): Promise<any> {
    pageOption.page = 0;
    try {
      await getCampaignList();
    } catch (e) {
      console.log(e);
    }
  }

  function handleTotalPage(pagination: any): void {
    pageOption.pageTotal = pagination?.page_count || 0;
  }

  async function getListData(id: number): Promise<any> {
    showEmpty.value = false;
    loading.value = true;
    pageOption.page++;
    const listParams = {
      ...baseParams,
      ...pageOption,
      query_list: ChartMetricList.map(item => item.field),
      [LevelFields[Level.campaign].ids]: [id],
      sort_stat: sortStat.value,
      sort_order: sortOrder.value,
      display_search_type: 2,
      campaign_system_origin: SYSTEM_ORIGIN_MAP.ALL
    };
    const { data = {} } = await store.dispatch('dashboard/getAdgroupList', listParams);
    handleTotalPage(data.pagination || {});
    const list = handleDataList(data.table || [], Level.adgroup);
    const curCampaignList = campaignStore.getters.campaignListData.value.campaignList;
    const nextCampaignList = curCampaignList.concat(list)
    campaignStore.mutations.setCampaignDataList(nextCampaignList);
    loading.value = false;
    showEmpty.value = !nextCampaignList.length;
  }

  function updateSearchList(newList: Array<string>): void {
    campaignStore.mutations.setCampaignDataList(newList);
  }

  function createCalendarCookie(timeRange: string, st: string, et: string): void {
    sessionStorageSetItem(CAMPAIGN_CALENDAR_COOKIE, `${timeRange}|${st}|${et}`);
    cookie_value.value = `${timeRange}|${st}|${et}`;
  }
  const setLifetimeRange = () => {
    return {
      newStartTime: getAccountSignTime(),
      newEndTime: today(),
      lifetime: 1,
      campaignCalendar: TimeRangeType.lifetime.i18n,
      cookiePrefix: TimeRangeType.lifetime.value
    };
  };
  const setTodayTimeRange = () => {
    return {
      newStartTime: today(),
      newEndTime: today(),
      lifetime: 0,
      campaignCalendar: TimeRangeType.today.i18n,
      cookiePrefix: TimeRangeType.today.value
    };
  };
  const setYesterdayTimeRange = () => {
    return {
      newStartTime: moment(today()).subtract(1, 'days').format(TIME_FORMAT),
      newEndTime: today(),
      lifetime: 0,
      campaignCalendar: TimeRangeType.yesterday.i18n,
      cookiePrefix: TimeRangeType.yesterday.value
    };
  };
  const setLast7DaysTimeRange = () => {
    return {
      newStartTime: moment(today()).subtract(7, 'days').format(TIME_FORMAT),
      newEndTime: moment(today()).subtract(1, 'days').format(TIME_FORMAT),
      lifetime: 0,
      campaignCalendar: TimeRangeType.last7days.i18n,
      cookiePrefix: TimeRangeType.last7days.value
    };
  };
  const DataByTimeRange = {
    [TimeRangeType.lifetime.value]: setLifetimeRange,
    [TimeRangeType.today.value]: setTodayTimeRange,
    [TimeRangeType.yesterday.value]: setYesterdayTimeRange,
    [TimeRangeType.last7days.value]: setLast7DaysTimeRange
  };
  function setStartAndEndTimes(newTimeRange: string = selectedRange.value): void {
    const {
      newStartTime = moment(today()).subtract(7, 'days').format(TIME_FORMAT),
      newEndTime = moment(today()).subtract(1, 'days').format(TIME_FORMAT),
      lifetime = 0,
      campaignCalendar = TimeRangeType.last7days.i18n,
      cookiePrefix = TimeRangeType.last7days.value
    } = DataByTimeRange[newTimeRange]?.();
    setTimeInfo(campaignCalendar, newStartTime, newEndTime, lifetime);
    createCalendarCookie(cookiePrefix, newStartTime, newEndTime);
  }

  return {
    updateSearchList,
    baseParams,
    today,
    yesterday,
    last7Days,
    dayType,
    signTime,
    getCampaignList,
    showEmpty,
    loading,
    pageOption,
    startTime,
    endTime,
    lifeTime,
    selectedRange,
    updateDataList,
    rawTableResponse,
    handleDataList,
    statusFilters,
    objectiveFilters,
    updateListWithFilters,
    sortList,
    getListData,
    CREATE_TIME,
    setStartAndEndTimes,
    setTimeInfo,
    createCalendarCookie,
    CAMPAIGN_CALENDAR_COOKIE,
    cookie_value,
    loadingList,
    resetList,
    searchKeyword,
  };
}
